import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Box, CircularProgress, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  useMediaQuery, useTheme, Card, CardContent, CardActionArea, Chip,
  InputAdornment, IconButton,
} from '@mui/material';
import { Close, Search as SearchIcon } from '@mui/icons-material';
import priceListApi, { PriceListItem } from '../services/priceListService';
import { fmtMoney } from '../utils/formatMoney';

interface ItemSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (item: PriceListItem) => void;
  companyTier: number;
  initialQuery?: string;
}

const TIER_FIELD_MAP: Record<number, keyof PriceListItem> = {
  1: 'tier1_price', 2: 'tier2_price', 3: 'tier3_price', 4: 'tier4_price', 5: 'tier5_price',
};

const ItemSearchDialog: React.FC<ItemSearchDialogProps> = ({ open, onClose, onSelect, companyTier, initialQuery }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [sku, setSku] = useState('');
  const [desc, setDesc] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [results, setResults] = useState<PriceListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (open) {
      priceListApi.categories().then(setCategories).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (open && initialQuery) {
      setSku(initialQuery);
      setDesc('');
      setCategory('');
      setResults([]);
      setSearched(false);
      setLoading(true);
      priceListApi.searchAdvanced({ q: initialQuery, limit: 50 })
        .then(data => { setResults(data); setSearched(true); })
        .catch(() => { setResults([]); setSearched(true); })
        .finally(() => setLoading(false));
    } else if (!open) {
      setSku('');
      setDesc('');
      setCategory('');
      setResults([]);
      setSearched(false);
    }
  }, [open, initialQuery]);

  const handleSearch = useCallback(async () => {
    if (!sku && !desc && !category) return;
    setLoading(true);
    setSearched(true);
    try {
      const data = await priceListApi.searchAdvanced({
        q: sku || undefined,
        description: desc || undefined,
        category: category || undefined,
        limit: 50,
      });
      setResults(data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [sku, desc, category]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const tierField = TIER_FIELD_MAP[companyTier] || 'tier1_price';

  const handleSelect = (item: PriceListItem) => {
    onSelect(item);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Item Search
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 1.5, mb: 2, mt: 1 }}>
          <TextField
            label="DFP SKU" size="small" value={sku}
            onChange={e => setSku(e.target.value)}
            onKeyDown={handleKeyDown}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
            }}
          />
          <TextField
            label="Description" size="small" value={desc}
            onChange={e => setDesc(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <TextField
            select label="Category" size="small" value={category}
            onChange={e => setCategory(e.target.value)}
          >
            <MenuItem value="">All categories</MenuItem>
            {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
        </Box>
        <Button
          variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}
          disabled={loading || (!sku && !desc && !category)}
          fullWidth={isMobile} sx={{ mb: 2 }}
        >
          Search
        </Button>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {results.map(item => (
              <Card key={item.id} variant="outlined">
                <CardActionArea onClick={() => handleSelect(item)}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle2" fontWeight={600}>{item.part_number}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {item.description || item.item_name || '-'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" label={`MSRP ${fmtMoney(item.us_list_price)}`} />
                      <Chip size="small" label={`Tier ${companyTier}: ${fmtMoney((item as any)[tierField])}`} variant="outlined" />
                      <Chip size="small" label={`Cost ${fmtMoney(item.dfp_cost)}`} variant="outlined" />
                    </Box>
                    {item.category && <Chip size="small" label={item.category} sx={{ mt: 0.5 }} />}
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
            {searched && results.length === 0 && (
              <Typography color="text.secondary" align="center" sx={{ py: 3 }}>No items found.</Typography>
            )}
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Part Number</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">MSRP</TableCell>
                    <TableCell align="right">Tier {companyTier}</TableCell>
                    <TableCell align="right">DFP Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map(item => (
                    <TableRow
                      key={item.id} hover sx={{ cursor: 'pointer' }}
                      onClick={() => handleSelect(item)}
                    >
                      <TableCell>{item.part_number}</TableCell>
                      <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.description || item.item_name || '-'}
                      </TableCell>
                      <TableCell>{item.category || '-'}</TableCell>
                      <TableCell align="right">{fmtMoney(item.us_list_price)}</TableCell>
                      <TableCell align="right">{fmtMoney((item as any)[tierField])}</TableCell>
                      <TableCell align="right">{fmtMoney(item.dfp_cost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {searched && results.length === 0 && (
              <Typography color="text.secondary" align="center" sx={{ py: 3 }}>No items found.</Typography>
            )}
          </>
        )}
      </DialogContent>
      {isMobile && (
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ItemSearchDialog;
